import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useResponseRating, type FeedbackSurface } from '../../lib/responseFeedback';

/** A quiet optional mark on an AI reply. Faint until someone is looking for it. */
export default function ResponseRating({
  businessId,
  surface,
  targetId,
  excerpt,
  tone = 'dark',
}: {
  businessId: string;
  surface: FeedbackSurface;
  targetId: string;
  excerpt: string;
  tone?: 'dark' | 'light';
}) {
  const { rating, choose } = useResponseRating(businessId, surface, targetId);
  // Idle stays faint. The selected thumb fills in green or red. The agents page
  // remaps zinc-200/300 onto nearly the same gray as zinc-600, so a zinc
  // "selected" color looked identical to idle.
  const idle = tone === 'light'
    ? 'text-gray-300 hover:bg-gray-100 hover:text-gray-500'
    : 'text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600';
  const button = (which: 'up' | 'down', label: string) => {
    const on = rating === which;
    const selected = which === 'up'
      ? 'bg-emerald-100 text-emerald-600'
      : 'bg-rose-100 text-rose-600';
    const Icon = which === 'up' ? ThumbsUp : ThumbsDown;
    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          void choose(which, excerpt);
        }}
        aria-label={label}
        aria-pressed={on}
        title={label}
        className={`rounded-full p-1 transition-colors ${on ? selected : idle}`}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={on ? 2.25 : 1.75} fill={on ? 'currentColor' : 'none'} />
      </button>
    );
  };
  return (
    <div className="flex items-center">
      {button('up', 'Good response')}
      {button('down', 'Poor response')}
    </div>
  );
}
